/* This util is intended for providing important "update" information to screen readers.
 * What to add:
 * - Brief, context-specific changes in the UI
 * - Summaries of swaths of similar changes across the page
 * - Just enough context for the user to tab into the component if they want more info.
 *
 * Good:
 * - Table "Guides" updated. Displaying 2 of 12 results.
 * - "Features" page content is loading.
 * - "Widget 1" encountered an error.
 *
 * Bad:
 * - Content is loading. (vague)
 * - "Widget 1" is loading. (lots of Dashboard widgets loading at once can overwhelm the screenreader)
 * - <re-reading the entire table after it updates>
 *
 * Usage:
 * `queueMessage({
 *     message: 'This will show up to the screen reader',
 *     highPriority: false,     // default; `true` will move it to the front of the queue
 *     timeWindow: 1000,        // default; max time (ms) given to read the message if several are queued
 *     ariaLiveMode: 'polite'   // default; use other modes with caution (e.g. 'assertive')
 *  })`
 */

import { effectScope, ref, readonly, watch } from 'vue';
import { useAriaLiveMessageQueue as useComponentMessageQueue } from '@pendo/components-vue3';
const DEFAULT_MESSAGE_TIME_WINDOW_MS = 1000;

let registry = [];

let flushing = false;

const text = ref('');
const mode = ref('polite');

const next = () => {
    return registry.shift();
};

const sleep = (ms) => {
    return new Promise((r) => setTimeout(r, ms));
};

const flushQueue = async () => {
    if (flushing) {
        return;
    }

    flushing = true;

    while (registry.length) {
        const { message, timeWindow, ariaLiveMode } = next();

        text.value = '';
        // eslint-disable-next-line no-await-in-loop
        await sleep(10);

        text.value = message || '';
        mode.value = ariaLiveMode || 'polite';

        // eslint-disable-next-line no-await-in-loop
        await sleep(timeWindow || DEFAULT_MESSAGE_TIME_WINDOW_MS);
    }

    flushing = false;
};

export const queueMessage = ({ highPriority, ...msg } = {}) => {
    if (registry.find((queued) => queued.message === msg.message)) {
        return;
    }

    if (highPriority) {
        registry.unshift(msg);
    } else {
        registry.push(msg);
    }

    flushQueue();
};

const bulkQueueMessages = (messages) => {
    if (!messages || !messages.length) {
        return;
    }

    messages.forEach((msg) => queueMessage(msg));
};

const clearQueue = () => {
    registry = [];
};

let _bridgeScope = null;
function ensureComponentMessageBridge () {
    if (_bridgeScope) {
        return;
    }

    _bridgeScope = effectScope(true);
    _bridgeScope.run(() => {
        const { queuedMessages: queuedComponentMessages, clearQueue: clearComponentQueue } = useComponentMessageQueue();

        watch(queuedComponentMessages, () => {
            if (!queuedComponentMessages.value?.length) {
                return;
            }

            bulkQueueMessages(queuedComponentMessages.value);
            clearComponentQueue();
        });
    });
}

export default function useAriaLiveRegistry () {
    ensureComponentMessageBridge();

    return {
        queueMessage,
        bulkQueueMessages,
        ariaLiveMode: readonly(mode),
        activeScreenReaderMessage: readonly(text),
        registry: readonly(registry),
        clearScreenReaderQueue: clearQueue,
    };
}
